import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid, IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import TopBar from "../../../components/TopBar";
import { Controller, useForm, useWatch } from "react-hook-form";
import CountrySelect from "../../../components/form/CountrySelect";
import OMNSubdivisionSelect from "../../../components/form/OMNSubdivisionSelect";
import RegistrationTypeSelect from "../../../components/form/RegistrationTypeSelect";
import { map } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import CustomTextInput from "../../../components/CustomTextInput";
import { showSnackbar } from "../../../api/snackbarSlice";
import LogoUpload from "../../../components/LogoUpload";
import { uploadLogoToCloud } from "../../../helper/UploadLogoToCloud";
import TaxSchemeSelect from "../../../components/form/TaxSchemeSelect";
import EndpointSchemeIdSelect from "../../../components/form/EndpointSchemeIdSelect";
import {
  getCountryOptionByCode,
  getRegistrationTypeOptionByCode,
  getSubdivisionOptionByCode,
  getEndpointSchemeIdByCode,
} from "../../../api/codelist/codeListsSlice";
import { OMN_COUNTRY_CODE } from "../../../constants/defaults";
import { yupResolver } from "@hookform/resolvers/yup";
import { BusinessProfileValidation } from "../../../validations/BusinessProfileValidation";
import CreateBusinessProfileBanner from "../../../components/CreateBusinessProfileBanner";
import CloseIcon from "@mui/icons-material/Close";

const DEFAULT_ENDPOINT_SCHEME_ID = "0248";

const formatPeppolIdentifier = (endpointSchemeId, endpointId) => {
  const schemeId = endpointSchemeId?.code ?? endpointSchemeId;
  if (!schemeId || !endpointId) {
    return "";
  }
  return `${schemeId}:${endpointId}`;
};

export default function BusinessProfileForm({
  onSubmit,
  onCancel,
  initialData = {},
  apiErrors = {},
  edit = false,
  details = false,
  readOnly = false,
  isLoading = false,
  showDeclaration = true,
}) {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const [showThirdPartyDetails, setShowThirdPartyDetails] = useState(
    Boolean(initialData?.third_party),
  );
  const dispatch = useDispatch();

  const countryOption = useSelector(
    getCountryOptionByCode(OMN_COUNTRY_CODE),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.postal_address?.country_subentity),
  );
  const schemeAgencyIdOption = useSelector(
    getRegistrationTypeOptionByCode(initialData.scheme_agency_id),
  );
  const passportIssuingCountryOption = useSelector(
    getCountryOptionByCode(initialData.passport_issuing_country_code),
  );
  const thirdPartyCountryOption = useSelector(
    getCountryOptionByCode(OMN_COUNTRY_CODE),
  );
  const thirdPartySubdivisionOption = useSelector(
    getSubdivisionOptionByCode(
      initialData?.third_party?.postal_address?.country_subentity,
    ),
  );
  const endpointSchemeOption = useSelector(
    getEndpointSchemeIdByCode(DEFAULT_ENDPOINT_SCHEME_ID),
  );


  const handleRegisterType = () => {
    setValue("company_id", "");
  };

  const handleThirdPartyCountryChange = (value) => {
    setValue(
      "third_party.postal_address.country_subentity",
      value?.code === OMN_COUNTRY_CODE ? null : "",
    );
  };
  const initialThirdPartyData= {
    ...initialData?.third_party,
    postal_address: {
      ...initialData?.third_party?.postal_address,
      country_code: thirdPartyCountryOption,
      country_subentity:(thirdPartySubdivisionOption ?? null),
    },
  }
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(BusinessProfileValidation),
    defaultValues: {
      ...initialData,
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name:
          initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: countryOption,
        country_subentity:
          initialData.postal_address?.country_code === OMN_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      endpoint_scheme_id: endpointSchemeOption ?? null,
      endpoint_id: initialData?.endpoint_id || "",
      declaration: false,
      requires_declaration: showDeclaration,
      requires_third_party: showThirdPartyDetails,
      third_party: initialThirdPartyData,
    },
  });

  useEffect(() => {
    setValue("requires_declaration", showDeclaration);
  }, [showDeclaration, setValue]);

  useEffect(() => {
    setValue('requires_third_party', showThirdPartyDetails)
  }, [showThirdPartyDetails, setValue]);

  useEffect(() => {
    if (initialData?.third_party) {
      setShowThirdPartyDetails(true);
    }
  }, [initialData]);

  useEffect(() => {
    if (!edit && !details) {
      return;
    }
    reset({
      ...initialData,
      postal_address: {
        ...initialData.postal_address,
        street_name: initialData.postal_address?.street_name,
        additional_street_name:
          initialData.postal_address?.additional_street_name,
        address_line: initialData.postal_address?.address_line,
        city_name: initialData.postal_address?.city_name,
        postal_zone: initialData.postal_address?.postal_zone,
        country_code: countryOption,
        country_subentity:
          initialData.postal_address?.country_code === OMN_COUNTRY_CODE
            ? (subdivisionOption ?? null)
            : initialData.postal_address?.country_subentity,
      },
      telephone: initialData.telephone,
      scheme_agency_id: schemeAgencyIdOption,
      passport_issuing_country_code: passportIssuingCountryOption,
      endpoint_scheme_id: endpointSchemeOption ?? null,
      endpoint_id: initialData?.endpoint_id || "",
      third_party: {
        ...initialData?.third_party,
        postal_address: {
          ...initialData?.third_party?.postal_address,
          country_code: thirdPartyCountryOption,
          country_subentity:
            initialData?.third_party?.postal_address?.country_code ===
            OMN_COUNTRY_CODE
              ? (thirdPartySubdivisionOption ?? null)
              : initialData?.third_party?.postal_address?.country_subentity,
        },
      },
      declaration: false,
      requires_declaration: showDeclaration,
      requires_third_party: showThirdPartyDetails,
    });
  }, [
    edit,
    details,
    initialData,
    countryOption,
    subdivisionOption,
    schemeAgencyIdOption,
    passportIssuingCountryOption,
    thirdPartyCountryOption,
    thirdPartySubdivisionOption,
    endpointSchemeOption,
    showDeclaration,
    reset,
  ]);

  useEffect(() => {
    if (!apiErrors || typeof apiErrors !== "object") {
      return;
    }
    map(apiErrors, (value, key) => {
      if (value) {
        setError(key, { type: "server", message: value });
      }
    });
  }, [setError, apiErrors]);

  const lockedVatIdentifier =
    initialData?.party_tax_scheme?.company_id ??
    initialData?.tax_identifier ??
    "";

  const handleFormSubmit = async (data) => {
    let logoUrl = initialData?.logo_url ?? "";

    if (file) {
      logoUrl = await uploadLogoToCloud(file);

      if (!logoUrl) {
        dispatch(
          showSnackbar({
            message: "Organization settings updated successfully",
            severity: "error",
          }),
        );
        return;
      }
    }

    const isOMN = data.postal_address?.country_code?.code === OMN_COUNTRY_CODE;
    const vatIdentifier = edit
      ? lockedVatIdentifier
      : data?.tax_identifier ||
        data?.party_tax_scheme?.company_id ||
        lockedVatIdentifier ||
        "";

    const submitData = {
      ...data,
      scheme_agency_id: data.scheme_agency_id?.code || undefined,
      endpoint_scheme_id: edit
        ? initialData?.endpoint_scheme_id ||
          (isOMN ? DEFAULT_ENDPOINT_SCHEME_ID : undefined)
        : data?.endpoint_scheme_id?.code ||
          (isOMN ? DEFAULT_ENDPOINT_SCHEME_ID : undefined),
      tax_identifier: isOMN ? vatIdentifier : undefined,
      company_id: data?.company_id || undefined,
      endpoint_id: edit
        ? initialData?.endpoint_id || (isOMN ? vatIdentifier : undefined)
        : data?.endpoint_id || (isOMN ? vatIdentifier : "") || "",
      logo_url: logoUrl,
      postal_address: {
        ...data.postal_address,
        country_code: data.postal_address?.country_code?.code,
        country: data.postal_address?.country_code?.label,
        country_subentity:
          data.postal_address?.country_code?.code === OMN_COUNTRY_CODE
            ? data.postal_address?.country_subentity?.code
            : data.postal_address?.country_subentity,
      },
      passport_issuing_country_code:
        data?.scheme_agency_id?.code === "PASNUM"
          ? data.passport_issuing_country_code?.code
          : null,
      third_party: data?.third_party
        ? {
            ...data.third_party,
            postal_address: {
              ...data.third_party?.postal_address,
              country_code:
                data.third_party?.postal_address?.country_code?.code,
              country: data.third_party?.postal_address?.country_code?.label,
              country_subentity:
                data.third_party?.postal_address?.country_code?.code ===
                OMN_COUNTRY_CODE
                  ? data.third_party?.postal_address?.country_subentity?.code
                  : data.third_party?.postal_address?.country_subentity,
            },
          }
        : null,
    };
    if (!showThirdPartyDetails) {
      submitData.third_party = null;
    }
    delete submitData.requires_declaration;
    delete submitData.requires_third_party;
    console.log(submitData.party_tax_scheme.tax_scheme)
    onSubmit(submitData);
  };

  const registrationType = useWatch({ control, name: "scheme_agency_id" });
  const endpointSchemeId = useWatch({ control, name: "endpoint_scheme_id" });
  const endpointId = useWatch({ control, name: "endpoint_id" });
  const vatId = useWatch({ control, name: "party_tax_scheme.company_id" });
  const countryCode = useWatch({
    control,
    name: "postal_address.country_code",
  })?.code;
  const thirdPartyCountryCode = useWatch({
    control,
    name: "third_party.postal_address.country_code",
  })?.code;

  const peppolIdentifier = useMemo(
    () =>
      formatPeppolIdentifier(
        endpointSchemeId ||
          initialData?.endpoint_scheme_id ||
          DEFAULT_ENDPOINT_SCHEME_ID,
        endpointId || vatId || initialData?.endpoint_id || "",
      ),
    [
      endpointSchemeId,
      endpointId,
      vatId,
      initialData?.endpoint_scheme_id,
      initialData?.endpoint_id,
    ],
  );

  useEffect(() => {
    if (countryCode === OMN_COUNTRY_CODE && vatId && !edit && !details) {
      setValue("endpoint_id", vatId);
      if (endpointSchemeOption) {
        setValue("endpoint_scheme_id", endpointSchemeOption);
      }
    }
  }, [countryCode, vatId, edit, details, setValue, endpointSchemeOption]);

  const taxSchemeFieldName =
    !details && !edit ? "tax_scheme" : "party_tax_scheme.tax_scheme";

  const registrationNumberLabel = registrationType
    ? `${registrationType.label} (${registrationType?.code})`
    : "Registration Number";

  return (
    <>
      <Paper elevation={0} sx={{ p: "1rem", mt: "-1rem", opacity: 1 }}>
        {(!initialData.status || initialData.status === "PENDING") &&
          details && <CreateBusinessProfileBanner />}

        {!readOnly && (
          <Box sx={{ pt: 2, pb: 4 }}>
            <Grid
              container
              sx={{
                display: "block",
              }}
            >
              <Grid>
                <TopBar
                  title={"Business Profile Form"}
                  identifier={initialData?.profile_id}
                >
                  <Box>
                    <Button
                      data-testid="cancel-button"
                      key={"cancel"}
                      variant="outlined"
                      onClick={onCancel}
                      disabled={isLoading}
                      sx={{ marginLeft: theme.spacing(1) }}
                    >
                      {"Cancel"}
                    </Button>
                    <Button
                      data-testid="business-profile-save"
                      key={"save"}
                      variant="contained"
                      onClick={handleSubmit(handleFormSubmit)}
                      disabled={isLoading}
                      sx={{ marginLeft: theme.spacing(1) }}
                    >
                      {"Submit"}
                    </Button>
                  </Box>
                </TopBar>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box>
          <Grid container spacing={2}>
            <Grid container spacing={2} size={{ xs: 4, sm: 8 }}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <CustomTextInput
                  data-testid="name-input"
                  name="name"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Name"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <CustomTextInput
                  data-testid="business-name-input"
                  name="party_name"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Business Name"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <CustomTextInput
                  data-testid="business-name-in-local-language-input"
                  name="party_name_in_local_language"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Business Name in Local Language"}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 12 }}>
                <CustomTextInput
                  data-testid="email-input"
                  name="email"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Email"}
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 4, sm: 4 }}>
              <LogoUpload
                initialLogoUrl={initialData?.logo_url}
                onFileChange={setFile}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
            {"Address"}
            <Divider></Divider>
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="street_name-input"
                name="postal_address.street_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Street Name"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="additional_street_name-input"
                name="postal_address.additional_street_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Additional Street Name"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="city_name-input"
                name="postal_address.city_name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"City Name"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="address_line-input"
                name="postal_address.address_line"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Address Line"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect
                data-testid="country_code-select"
                name="postal_address.country_code"
                control={control}
                errors={errors}
                disabled={true}
                label={"Country"}
                helperText={
                  edit ? "Country cannot be changed" : ""
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {countryCode === OMN_COUNTRY_CODE ? (
                <OMNSubdivisionSelect
                  data-testid="country_subentity-select"
                  name="postal_address.country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Country Subdivision"}
                />
              ) : (
                <CustomTextInput
                  data-testid="country_subentity-input"
                  name="postal_address.country_subentity"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  label={"Country Subdivision"}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="postal_zone-input"
                name="postal_address.postal_zone"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Postal Zone"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-tetsid="telephone-input"
                name="telephone"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={"Telephone"}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
            {"Business Identifiers"}
            <Divider></Divider>
          </Typography>

          <Grid container spacing={2}>
            {countryCode === OMN_COUNTRY_CODE && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TaxSchemeSelect
                    data-testid="tax_scheme-select"
                    name={taxSchemeFieldName}
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Tax Scheme"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="tax_identifier-input"
                    name="party_tax_scheme.company_id"
                    control={control}
                    errors={errors}
                    readOnly={edit || details}
                    label={"VAT"}
                    helperText={
                      edit ? "VAT cannot be changed after creation" : ""
                    }
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <RegistrationTypeSelect
                data-testid="rscheme_agency_id-select"
                errors={errors}
                readOnly={readOnly}
                control={control}
                name="scheme_agency_id"
                onChange={handleRegisterType}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="registration_number-input"
                name="company_id"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={registrationNumberLabel}
              />
            </Grid>
            {registrationType?.code === "PASNUM" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <CountrySelect
                  data-testid="passport_issuing_country_code-select"
                  name="passport_issuing_country_code"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                  disabled={registrationType?.code !== "PASNUM"}
                  label={"Passport Issuing Country"}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <EndpointSchemeIdSelect
                data-testid="endpoint_scheme_id-select"
                name="endpoint_scheme_id"
                control={control}
                errors={errors}
                readOnly={readOnly || edit || details}
                disabled={
                  readOnly ||
                  edit ||
                  details ||
                  countryCode === OMN_COUNTRY_CODE
                }
                label={"Endpoint Scheme Id"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CustomTextInput
                data-testid="endpoint_id-input"
                name="endpoint_id"
                control={control}
                errors={errors}
                readOnly={
                  readOnly ||
                  edit ||
                  details ||
                  countryCode === OMN_COUNTRY_CODE
                }
                label={"Endpoint Id"}
                helperText={
                  countryCode === OMN_COUNTRY_CODE
                    ? "Synced from VAT for Oman profiles"
                    : edit
                      ? "Endpoint id cannot be changed after creation"
                      : undefined
                }
              />
            </Grid>
          </Grid>

          {(readOnly || details) && (
            <>
              <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
                {"Peppol Identifier"}
                <Divider></Divider>
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    data-testid="peppol-identifier"
                    label="Peppol Identifier"
                    value={peppolIdentifier}
                    variant="outlined"
                    fullWidth
                    InputProps={{ readOnly: true }}
                    helperText={
                      peppolIdentifier ? undefined : "Derived from VAT on save"
                    }
                  />
                </Grid>
              </Grid>
            </>
          )}
          {showThirdPartyDetails && (
            <>
              <Box  sx={{pt: "1rem", display: "flex" , justifyContent: "space-between"}}>
                <Typography variant="h6" >
                  {"Third Party Details"}
                </Typography>
                <IconButton onClick={()=>setShowThirdPartyDetails(false)} title={"Remove Third Party Details"}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider></Divider>

              <Grid container spacing={2} sx={{mt: "1.5rem"}}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-name-input"
                    name="third_party.name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Third Party Name"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-vat-id-input"
                    name="third_party.vat_identifier"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Third Party VAT ID"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TaxSchemeSelect
                    data-testid="third-party-tax_scheme-select"
                    name="third_party.tax_scheme_code"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Third Party Tax Scheme Code"}
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: "1.5rem", pt: "1rem" }}>
                {"Third Party Address"}
                <Divider></Divider>
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-street-name-input"
                    name="third_party.postal_address.street_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Street Name"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-additional-street-name-input"
                    name="third_party.postal_address.additional_street_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Additional Street Name"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-city-name-input"
                    name="third_party.postal_address.city_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"City Name"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-address-line-input"
                    name="third_party.postal_address.address_line"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Address Line"}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CountrySelect
                    data-testid="third-party-country-code-select"
                    name="third_party.postal_address.country_code"
                    control={control}
                    errors={errors}
                    disabled={true}
                    label={"Country"}
                    onChange={handleThirdPartyCountryChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  {thirdPartyCountryCode === OMN_COUNTRY_CODE ? (
                    <OMNSubdivisionSelect
                      data-testid="third-party-country-subentity-select"
                      name="third_party.postal_address.country_subentity"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                      label={"Country Subdivision"}
                    />
                  ) : (
                    <CustomTextInput
                      data-testid="third-party-country-subentity-input"
                      name="third_party.postal_address.country_subentity"
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                      label={"Country Subdivision"}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <CustomTextInput
                    data-testid="third-party-postal-zone-input"
                    name="third_party.postal_address.postal_zone"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label={"Postal Zone"}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {!details && (
              <>
                { !showThirdPartyDetails && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Button
                          data-testid="toggle-third-party-details"
                          variant="contained"
                          onClick={()=>setShowThirdPartyDetails(true)}
                          disabled={readOnly}
                      >
                        {"Add Third Party Details"}
                      </Button>
                    </Box>
                )}
              </>
          )}
          {!readOnly && showDeclaration && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Controller
                name="declaration"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          color="primary"
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                          }}
                        />
                      }
                      label={
                        "I declare that all information provided is true, accurate and complete to the best of my knowledge and belief"
                      }
                    />
                    {errors.declaration && (
                      <FormHelperText error sx={{ ml: 0 }}>
                        {errors.declaration.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </>
  );
}
